import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, Button } from '@scio3/ui';

type ChangePlanWarning = {
  messageDisplay?: string;
  textValues?: string[];
  requiredRemove?: boolean;
  url?: string;
  plan?: string;
};

type Props = {
  open: boolean;
  warnings: ChangePlanWarning[];
  onDismiss: () => void;
  onConfirm: () => void;
  /** Custom label for the confirm button (default: 'Next') */
  confirmLabel?: string;
};

/**
 * Plan change warning dialog — matching scio1 ConfirmDowngradeSubscriptionModal.
 * Shows when transitioning from PLAN step with a different plan selected.
 * Backend validateChangePlan provides dynamic warning messages.
 */
export function ChangePlanWarningDialog({
  open,
  warnings,
  onDismiss,
  onConfirm,
  confirmLabel,
}: Props) {
  const { t } = useTranslation();

  const hasRequiredRemove = warnings.some((w) => w.requiredRemove);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onDismiss();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        data-testid="change-plan-warning-dialog"
      >
        {/* Orange warning header — matching scio1 bg-warning */}
        <DialogHeader className="px-6 py-3 rounded-t-lg bg-amber-500 text-white space-y-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-white">
              <span>⚠</span>
            </DialogTitle>
            <DialogClose
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-white/80 hover:bg-white/20 hover:text-white transition-colors"
              aria-label={t('Close')}
            >
              <X className="h-4 w-4" />
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="px-6 py-4 space-y-3">
          {warnings.map((warning, idx) => (
            <div key={idx}>
              <p className="text-sm font-medium">
                {t(warning.messageDisplay ?? '', { plan: warning.plan ?? '' })}
                {/* "Go To" button — shown when requiredRemove + url, matching scio1 */}
                {warning.requiredRemove && warning.url && (
                  <a
                    href={warning.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-block"
                  >
                    <Button size="sm" className="cursor-pointer">
                      {t('Go To')}
                    </Button>
                  </a>
                )}
              </p>
              {warning.textValues && warning.textValues.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {warning.textValues.map((text, i) => (
                    <p key={i} className="text-sm text-muted-foreground">
                      {' '}
                      - {t(text)}
                    </p>
                  ))}
                </div>
              )}
              {idx < warnings.length - 1 && <hr className="mt-3" />}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-3 px-6 pb-4">
          <Button variant="outline" onClick={onDismiss} className="cursor-pointer">
            {hasRequiredRemove ? t('Close') : t('Cancel')}
          </Button>
          {!hasRequiredRemove && (
            <Button onClick={onConfirm} className="cursor-pointer">
              {confirmLabel ?? t('Next')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
